// Wayne AI Platform — WebSocket bridge.
//
// One bridge per browser tab. Wraps `partysocket.ReconnectingWebSocket` and:
//   - Fetches a fresh signed URL from `POST /api/ws/ticket` every time the
//     underlying WebSocket needs to (re)open. The URL is single-use and
//     short-lived, so we never cache it.
//   - Validates every inbound frame against the locked v:1 envelope (Zod).
//     Invalid frames are logged and dropped — they never reach the reducer.
//   - Tracks `lastSeq` per session. On reopen we send
//     `{ type: 'replay', fromSeq: lastSeq + 1 }`. If the server's first event
//     after replay leaves a gap > 200 we expect the server to push a
//     `system.snapshot`; we forward it to the reducer for a hard reset.
//   - Heartbeat: server emits `system.heartbeat` every 25s. The client asserts
//     no event for 10s (HEARTBEAT_INTERVAL_MS + HEARTBEAT_GRACE_MS = 35s) and
//     force-closes; partysocket reconnects automatically.
//   - WS close code 4401 → full-page redirect to Midway (mirrors HTTP 401).

import ReconnectingWebSocket from 'partysocket/ws';
import {
  decodeServerEvent,
  encodeClientEvent,
  HEARTBEAT_GRACE_MS,
  HEARTBEAT_INTERVAL_MS,
  WS_CLOSE_CODE_UNAUTHORIZED,
  type ClientEvent,
  type ServerEvent,
} from './types';
import { redirectToLogin } from '@/api/client';
import { createWsTicket } from '@/api/generated';

/** Reconnection settings — partysocket defaults are fine for the server's
 * heartbeat cadence; we keep them explicit so future tuning is grep-able. */
const RECONNECT_OPTIONS = {
  minReconnectionDelay: 1_000,
  maxReconnectionDelay: 10_000,
  reconnectionDelayGrowFactor: 1.3,
  // partysocket treats `Infinity` as unlimited retries — we want this for a
  // long-running console; the user's tab can stay open all day.
  maxRetries: Infinity,
  // Don't open connections that won't accept frames within 10s.
  connectionTimeout: 10_000,
} as const;

export type WsStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed';

export interface WsBridgeOptions {
  /** Override the ticket-mint endpoint (used by tests). */
  ticketProvider?: () => Promise<string>;
  /** Custom WebSocket constructor (used by tests with mock-socket). */
  WebSocketImpl?: typeof WebSocket;
  /** Default sessionId to subscribe to once the socket opens. */
  sessionId?: string;
  /** Called for every validated `ServerEvent` — typically the chatStream
   * store's `applyEvent`. */
  onEvent: (event: ServerEvent) => void;
  /** Called whenever the connection state changes (UI status pill). */
  onStatusChange?: (status: WsStatus) => void;
  /** Optional: invoked when the server sends an event that fails Zod. */
  onInvalidEvent?: (raw: unknown, error: unknown) => void;
}

/** Internal: how we look up the last seen seq per session. */
type SeqTracker = Map<string, number>;

/**
 * Resolve a fresh signed URL for the WebSocket connection. We call
 * `POST /api/ws/ticket` from the cookied HTTPS session; the server returns
 * `{ url, expiresInSeconds }`.
 *
 * On failure, we throw so that partysocket's built-in backoff
 * (maxReconnectionDelay) governs retry timing. The WsBridgeProvider applies
 * an additional reconnect-throttle to prevent infinite loops when the
 * ticket endpoint is persistently broken.
 */
async function defaultTicketProvider(): Promise<string> {
  try {
    const result = await createWsTicket();
    if ('error' in result && result.error !== undefined) {
      throw new Error('Failed to obtain WS ticket');
    }
    if (!result.data?.url) {
      throw new Error('Ticket response missing url');
    }
    return result.data.url;
  } catch (err) {
    // Log for observability — the caller (partysocket) will schedule a
    // retry with exponential backoff up to maxReconnectionDelay.
    console.warn('[WsBridge] Ticket provider failed:', err);
    throw err;
  }
}

/** Wayne WebSocket bridge. Construct with `new WsBridge(options)`. */
export class WsBridge {
  private socket: ReconnectingWebSocket | null = null;
  private status: WsStatus = 'idle';
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private seqTracker: SeqTracker = new Map();
  private intentionalClose = false;
  private currentSessionId: string | undefined;

  constructor(private readonly options: WsBridgeOptions) {
    if (options.sessionId !== undefined) {
      this.currentSessionId = options.sessionId;
    }
  }

  /** Open the connection. Idempotent. */
  open(): void {
    if (this.socket) return;
    this.intentionalClose = false;
    this.setStatus('connecting');

    const ticketProvider = this.options.ticketProvider ?? defaultTicketProvider;
    const wsCtor = this.options.WebSocketImpl;

    const rwsOptions: Record<string, unknown> = { ...RECONNECT_OPTIONS };
    if (wsCtor !== undefined) {
      // `partysocket`'s Options.WebSocket is `any`; we widen here.
      rwsOptions.WebSocket = wsCtor;
    }

    // ReconnectingWebSocket accepts `() => string | Promise<string>` as the
    // url provider — re-invoked on every (re)connect attempt.
    this.socket = new ReconnectingWebSocket(ticketProvider, null, rwsOptions);

    this.socket.addEventListener('open', this.handleOpen);
    this.socket.addEventListener('message', this.handleMessage);
    this.socket.addEventListener('close', this.handleClose);
    this.socket.addEventListener('error', this.handleError);
  }

  /** Close the connection — used on logout and tab unmount. */
  close(): void {
    this.intentionalClose = true;
    this.clearHeartbeat();
    if (this.socket) {
      this.socket.removeEventListener('open', this.handleOpen);
      this.socket.removeEventListener('message', this.handleMessage);
      this.socket.removeEventListener('close', this.handleClose);
      this.socket.removeEventListener('error', this.handleError);
      this.socket.close();
      this.socket = null;
    }
    this.setStatus('closed');
  }

  /** Subscribe to a session. Server begins streaming; idempotent on the
   * server side. */
  subscribe(sessionId: string): void {
    this.currentSessionId = sessionId;
    this.send({ v: 1, type: 'subscribe', sessionId });
  }

  /** Unsubscribe from a session. */
  unsubscribe(sessionId: string): void {
    this.send({ v: 1, type: 'unsubscribe', sessionId });
  }

  /** Acknowledge the highest seen seq — the server uses this to trim its
   * replay buffer. Best-effort; we don't fail if the socket isn't open. */
  ack(sessionId: string, seq: number): void {
    this.send({ v: 1, type: 'ack', sessionId, fromSeq: seq });
  }

  /** Snapshot of the current connection state. */
  getStatus(): WsStatus {
    return this.status;
  }

  /** Last seen seq for a given sessionId, or 0 if unknown. */
  getLastSeq(sessionId: string): number {
    return this.seqTracker.get(sessionId) ?? 0;
  }

  // -- internals -------------------------------------------------------------

  private send(payload: ClientEvent): void {
    if (!this.socket || this.socket.readyState !== ReconnectingWebSocket.OPEN) {
      return;
    }
    this.socket.send(encodeClientEvent(payload));
  }

  private setStatus(next: WsStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.options.onStatusChange?.(next);
  }

  private resetHeartbeatWatchdog = (): void => {
    this.clearHeartbeat();
    // If we don't see ANY frame (heartbeat or otherwise) within
    // 25s + 10s we force-close; partysocket reconnects.
    this.heartbeatTimer = setTimeout(
      () => {
        if (
          this.socket &&
          this.socket.readyState === ReconnectingWebSocket.OPEN
        ) {
          // Force-close so partysocket reconnects.
          this.socket.reconnect();
        }
      },
      HEARTBEAT_INTERVAL_MS + HEARTBEAT_GRACE_MS,
    );
  };

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private handleOpen = (): void => {
    this.setStatus('open');
    this.resetHeartbeatWatchdog();
    if (this.currentSessionId !== undefined) {
      const lastSeq = this.seqTracker.get(this.currentSessionId) ?? 0;
      if (lastSeq > 0) {
        // Resuming after a reconnect — request replay from where we left off.
        this.send({
          v: 1,
          type: 'replay',
          sessionId: this.currentSessionId,
          fromSeq: lastSeq + 1,
        });
      } else {
        this.send({
          v: 1,
          type: 'subscribe',
          sessionId: this.currentSessionId,
        });
      }
    }
  };

  private handleMessage = (event: MessageEvent): void => {
    this.resetHeartbeatWatchdog();
    let raw: unknown;
    try {
      raw =
        typeof event.data === 'string'
          ? JSON.parse(event.data)
          : null;
    } catch (err) {
      this.options.onInvalidEvent?.(event.data, err);
      return;
    }
    const decoded = decodeServerEvent(raw);
    if (!decoded) {
      this.options.onInvalidEvent?.(raw, new Error('schema validation failed'));
      return;
    }

    // Track the highest seq we've seen for this session. We accept
    // `system.snapshot` resetting the seq downward (it's authoritative).
    const previous = this.seqTracker.get(decoded.sessionId) ?? 0;
    if (decoded.type === 'system.snapshot') {
      this.seqTracker.set(decoded.sessionId, decoded.seq);
    } else if (decoded.seq > previous) {
      this.seqTracker.set(decoded.sessionId, decoded.seq);
    } else {
      // Out-of-order or duplicate event — drop it. The reducer must remain
      // monotonic.
      return;
    }

    this.options.onEvent(decoded);
  };

  private handleClose = (event: CloseEvent): void => {
    this.clearHeartbeat();
    if (event.code === WS_CLOSE_CODE_UNAUTHORIZED) {
      // Mirror the HTTP 401 path.
      redirectToLogin();
      return;
    }
    if (this.intentionalClose) {
      this.setStatus('closed');
    } else {
      this.setStatus('reconnecting');
    }
  };

  private handleError = (): void => {
    // partysocket already schedules a reconnect; we just surface the status.
    if (!this.intentionalClose) {
      this.setStatus('reconnecting');
    }
  };
}

/** Convenience factory for the singleton bridge used by the chat surface. */
export function createWsBridge(options: WsBridgeOptions): WsBridge {
  return new WsBridge(options);
}
